import React, { FC, useCallback, useMemo } from 'react';

import { observer } from 'mobx-react';
import { Tooltip } from 'antd';
import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';

import PolicySelect from '../../../Common/PolicySelect';
import PolicyDescription from '../../../Common/PolicyDescription';
import BlockedRecipientsSection, { BlockedRecipient } from '../../../Common/BlockedRecipientsSection';
import MissingDetailsSection from './MissingDetails';
import UsersWithDetailsSection from '../../../Common/MissingDetails/UsersWithDetailsSection';
import useMissingDetailsState from '../../../Common/MissingDetails/useMissingDetailsState';
import { useStores } from '../../../hooks';
import { getPolicyAggregate } from '../../../utils';
import { FolderToDisplay, PolicySimpleWithMFA, UploadedItem } from '../../../../stores/UploadFilesStore/interfaces';
import { ApplyRulesResponse } from '../../../../api/requests';
import {
    REQUIRED_PHONE_VERIFICATION,
    REQUIRED_PASSWORD_VERIFICATION,
    PASSWORD_PROTECTION,
} from '../../../../consts';
import styles from './PolicyStep.module.scss';

const nameSpace = 'uploadFiles.policyStep';

interface PolicyStepProps {
    rulesResults: ApplyRulesResponse;
    requiresPhoneVerification: boolean;
    requiresPasswordVerification: boolean;
}

interface GroupedPolicy {
    policyId: string;
    ruleName: string;
    fileIds: string[];
}

const PolicyStep: FC<PolicyStepProps> = observer(({
    rulesResults,
    requiresPhoneVerification,
    requiresPasswordVerification,
}) => {
    const { t } = useTranslation();
    const {
        uploadFilesStore,
        policyStore: { policyList },
    } = useStores();

    const {
        successfullyUploadedItems,
        modifiedFoldedFiles,
        completedFiles,
    } = uploadFilesStore;

    // Get all rules with action "policy"
    const policyRules = useMemo(() =>
        rulesResults.filter((r) => r.action === 'policy' && r.policy_id),
        [rulesResults],
    );

    // File IDs that have a policy rule assigned
    const fileIdsWithPolicyRule = useMemo(() =>
        new Set(policyRules.map((r) => r.file_id)),
        [policyRules],
    );

    // Map folder fid -> individual files inside that folder
    const folderFilesMap = useMemo(() => {
        const map = new Map<string, { filename: string; fid: string }[]>();

        successfullyUploadedItems.forEach((item) => {
            if (item.isFolder && item.fid) {
                const folderDisplay = modifiedFoldedFiles.find(
                    (f): f is FolderToDisplay => f.isFolder && (f as FolderToDisplay).uid === item.uid,
                );

                if (folderDisplay?.foldedFiles) {
                    const files = folderDisplay.foldedFiles
                        .filter((f) => f.status === 'done')
                        .map((f) => ({ filename: f.filename || f.name || f.fid, fid: f.fid }));
                    if (files.length > 0) {
                        map.set(item.fid, files);
                    }
                }
            }
        });

        return map;
    }, [successfullyUploadedItems, modifiedFoldedFiles]);

    // Files/folders that do NOT have any policy rule — need "Choose Policy"
    // Folders are always included so users can set the folder's policy for future files
    const filesWithoutPolicyRule = useMemo(() =>
        successfullyUploadedItems.filter((file) => !fileIdsWithPolicyRule.has(file.fid)),
        [successfullyUploadedItems, fileIdsWithPolicyRule],
    );

    const hasFilesWithoutPolicyRule = filesWithoutPolicyRule.length > 0;

    // Group policy rules by policy_id
    const groupedPolicies = useMemo(() => {
        const groups: Map<string, GroupedPolicy> = new Map();

        policyRules.forEach((rule) => {
            const key = rule.policy_id;
            if (!groups.has(key)) {
                groups.set(key, {
                    policyId: rule.policy_id,
                    ruleName: rule.rule,
                    fileIds: [],
                });
            }
            const group = groups.get(key);
            if (!group.fileIds.includes(rule.file_id)) {
                group.fileIds.push(rule.file_id);
            }
        });

        return Array.from(groups.values());
    }, [policyRules]);

    const hasAssignedPolicies = groupedPolicies.length > 0;

    // Check if any assigned policy also requires phone/password
    const assignedPolicyRequiresPhone = useMemo(() => {
        return groupedPolicies.some((group) => {
            const policy = policyList.find((p) => p.id === group.policyId);
            if (!policy?.settings?.access) return false;
            return policy.settings.access.some(
                (s) => s.id === REQUIRED_PHONE_VERIFICATION && s.value,
            );
        });
    }, [groupedPolicies, policyList]);

    const assignedPolicyRequiresPassword = useMemo(() => {
        return groupedPolicies.some((group) => {
            const policy = policyList.find((p) => p.id === group.policyId);
            if (!policy?.settings?.access) return false;
            return policy.settings.access.some(
                (s) => (s.id === REQUIRED_PASSWORD_VERIFICATION || s.id === PASSWORD_PROTECTION) && s.value,
            );
        });
    }, [groupedPolicies, policyList]);

    const showPhones = requiresPhoneVerification || assignedPolicyRequiresPhone;
    const showPasswords = requiresPasswordVerification || assignedPolicyRequiresPassword;
    const hasMissingDetails = showPhones || showPasswords;

    const {
        detailsLoaded,
        missingRecipients,
        filledRecipients,
    } = useMissingDetailsState({ showPhones, showPasswords });

    // Get policy details helper
    const getPolicyById = useCallback((policyId: string) => {
        return policyList.find((p) => p.id === policyId);
    }, [policyList]);

    const getNewPolicy = (policyId: string): PolicySimpleWithMFA => {
        const newPolicy = policyList.find(({ id }) => id === policyId);
        return { id: newPolicy.id, title: newPolicy.title, isMfa: newPolicy.isMfa };
    };

    const onChangePolicy = useCallback(async (newPolicyId: string): Promise<void> => {
        const newPolicy = getNewPolicy(newPolicyId);
        if (!newPolicy) return;

        // Apply policy to ALL files without a policy rule
        await Promise.all(
            filesWithoutPolicyRule.map((file) =>
                uploadFilesStore.changePolicy({
                    newPolicy,
                    fileId: file.fid,
                    isFolder: file.isFolder,
                    isExternalStorage: false,
                }),
            ),
        );
    }, [filesWithoutPolicyRule]);

    // Build a lookup map of all file IDs to names, including files nested inside folders
    const fileNameMap = useMemo(() => {
        const map = new Map<string, string>();
        successfullyUploadedItems.forEach((item) => {
            if (item.filename) map.set(item.fid, item.filename);
        });
        completedFiles.forEach((f) => {
            if (!map.has(f.fid) && (f.filename || f.name)) {
                map.set(f.fid, f.filename || f.name);
            }
        });
        folderFilesMap.forEach((files) => {
            files.forEach((f) => {
                if (!map.has(f.fid) && f.filename) {
                    map.set(f.fid, f.filename);
                }
            });
        });
        return map;
    }, [successfullyUploadedItems, completedFiles, folderFilesMap]);

    const getFileName = useCallback((fileId: string): string => {
        return fileNameMap.get(fileId) || fileId;
    }, [fileNameMap]);

    // Expand file IDs: if a fid is a folder, replace it with individual file entries
    const expandFileIds = useCallback((fileIds: string[]): { filename: string; fid: string }[] => {
        const result: { filename: string; fid: string }[] = [];
        fileIds.forEach((fid) => {
            const folderFiles = folderFilesMap.get(fid);
            if (folderFiles?.length) {
                result.push(...folderFiles);
            } else {
                result.push({ filename: getFileName(fid), fid });
            }
        });
        return result;
    }, [folderFilesMap, getFileName]);

    const getItemSummary = useCallback((items: UploadedItem[]): string => {
        const folderCount = items.filter((i) => i.isFolder).length;
        const fileCount = items.filter((i) => !i.isFolder).length;
        const parts: string[] = [];
        if (folderCount > 0) {
            parts.push(t(`${nameSpace}.folderCount`, { count: folderCount }));
        }
        if (fileCount > 0) {
            parts.push(t(`${nameSpace}.fileCount`, { count: fileCount }));
        }
        return parts.join(` ${t(`${nameSpace}.and`)} `);
    }, [t]);

    // Group blocked users: determine which recipients are blocked from all vs some files
    const { usersBlockedFromAll, usersBlockedFromSome } = useMemo(() => {
        const blockRules = rulesResults.filter((r) => r.action === 'block');
        const allFileIds = new Set(successfullyUploadedItems.map((f) => f.fid));

        // For each recipient, collect which files they are blocked from
        const blockedFilesPerRecipient = new Map<string, Set<string>>();

        blockRules.forEach((rule) => {
            rule.recipients?.forEach((email) => {
                if (!blockedFilesPerRecipient.has(email)) {
                    blockedFilesPerRecipient.set(email, new Set());
                }
                blockedFilesPerRecipient.get(email).add(rule.file_id);
            });
        });

        const fromAll: string[] = [];
        const fromSome: Array<{ email: string; fileIds: string[] }> = [];

        blockedFilesPerRecipient.forEach((blockedFileIds, email) => {
            // If the recipient is blocked from every uploaded file → "blocked from all"
            const allBlocked = allFileIds.size > 0
                && [...allFileIds].every((fid) => blockedFileIds.has(fid));
            if (allBlocked) {
                fromAll.push(email);
            } else {
                fromSome.push({ email, fileIds: [...blockedFileIds] });
            }
        });

        return { usersBlockedFromAll: fromAll, usersBlockedFromSome: fromSome };
    }, [rulesResults, successfullyUploadedItems]);

    const hasBlockedUsers = usersBlockedFromAll.length > 0 || usersBlockedFromSome.length > 0;

    const blockedRecipients: BlockedRecipient[] = useMemo(() => {
        const result: BlockedRecipient[] = [];
        usersBlockedFromAll.forEach((email) => {
            result.push({ email, blockedFromAll: true, fileIds: [] });
        });
        usersBlockedFromSome.forEach(({ email, fileIds }) => {
            result.push({ email, blockedFromAll: false, fileIds });
        });
        return result;
    }, [usersBlockedFromAll, usersBlockedFromSome]);

    const getFileIsFolder = useCallback((fileId: string): boolean => {
        const item = successfullyUploadedItems.find((i) => i.fid === fileId);
        return !!item?.isFolder;
    }, [successfullyUploadedItems]);

    // Get selected policy for Choose Policy section (use first file without policy rule as reference)
    const firstFileWithoutRule = filesWithoutPolicyRule[0] || null;
    const selectedPolicyForChoose = firstFileWithoutRule
        ? policyList.find((policyItem) => firstFileWithoutRule.policy?.id === policyItem.id)
        : null;
    const policyAggregateForChoose = selectedPolicyForChoose ? getPolicyAggregate(selectedPolicyForChoose) : null;

    if (!hasFilesWithoutPolicyRule && !hasAssignedPolicies && !hasBlockedUsers && !hasMissingDetails) {
        return null;
    }

    return (
        <div className={styles['policy-step']}>
            {/* Choose Policy Section - shown when there are files without a policy rule */}
            {hasFilesWithoutPolicyRule && (
                <div className={styles['policy-section']}>
                    <div className={styles['section-header']}>
                        <span className={styles['section-title']}>
                            {t(`${nameSpace}.choosePolicy`)}
                        </span>
                    </div>
                    <div className={styles['section-content']}>
                        <div className={styles['policy-layout']}>
                            <div className={styles['policy-left']}>
                                <PolicySelect
                                    onChange={onChangePolicy}
                                    selectedPolicyId={firstFileWithoutRule?.policy?.id}
                                    className={styles['policy-select']}
                                    data-testid="uploadDrawer_policyDropdown"
                                />
                                {filesWithoutPolicyRule.length > 0 && (() => {
                                    const summary = getItemSummary(filesWithoutPolicyRule);
                                    return (
                                        <Tooltip
                                            placement="top"
                                            color="#091F56"
                                            title={(
                                                <div>
                                                    {filesWithoutPolicyRule.map((item) => (
                                                        <div key={item.fid ?? item.uid} style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.6 }}>
                                                            {item.filename}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        >
                                            <div className={styles['assigned-info']}>
                                                <Trans
                                                    t={t}
                                                    i18nKey={`${nameSpace}.willBeAssignedTo`}
                                                    values={{ summary }}
                                                >
                                                    Will be assigned to
                                                    <span className={styles['assigned-link']}>
                                                        {'{{summary}}'}
                                                    </span>
                                                </Trans>
                                            </div>
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                            <div className={styles['policy-right']}>
                                {policyAggregateForChoose && (
                                    <PolicyDescription policyAggregate={policyAggregateForChoose} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assigned Policies Section - for files with policy rules */}
            {hasAssignedPolicies && (
                <div className={styles['assigned-policies-section']}>
                    <div className={styles['section-header']}>
                        <LockOutlined className={styles['section-icon']} />
                        <span className={styles['section-title']}>
                            {t(`${nameSpace}.assignedPolicies`)}
                        </span>
                    </div>

                    {groupedPolicies.map((group) => {
                        const policy = getPolicyById(group.policyId);
                        const policyAggregate = policy ? getPolicyAggregate(policy) : null;

                        return (
                            <div key={group.policyId} className={styles['assigned-policy-item']}>
                                <div className={styles['policy-layout']}>
                                    <div className={styles['policy-left']}>
                                        <PolicySelect
                                            selectedPolicyId={group.policyId}
                                            className={styles['policy-select']}
                                            disabled
                                        />
                                        <div className={styles['rule-info']}>
                                            <Trans
                                                t={t}
                                                i18nKey={`${nameSpace}.assignedDueTo`}
                                                values={{ ruleName: group.ruleName }}
                                            >
                                                Assigned due to
                                                <strong>{'{{ruleName}}'}</strong>
                                            </Trans>
                                        </div>
                                        {(() => {
                                            const expandedFiles = expandFileIds(group.fileIds);
                                            return (
                                                <Tooltip
                                                    placement="top"
                                                    color="#091F56"
                                                    title={(
                                                        <div>
                                                            {expandedFiles.map((file) => (
                                                                <div key={file.fid} style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.6 }}>
                                                                    {file.filename}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                >
                                                    <div className={styles['assigned-info']}>
                                                        <Trans
                                                            t={t}
                                                            i18nKey={`${nameSpace}.assignedToItems`}
                                                            values={{ count: expandedFiles.length }}
                                                        >
                                                            Assigned to
                                                            <span className={styles['assigned-link']}>
                                                                {'{{count}} items'}
                                                            </span>
                                                        </Trans>
                                                    </div>
                                                </Tooltip>
                                            );
                                        })()}
                                    </div>
                                    <div className={styles['policy-right']}>
                                        {policyAggregate && (
                                            <PolicyDescription policyAggregate={policyAggregate} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Loading indicator while fetching details */}
            {hasMissingDetails && !detailsLoaded && (
                <div className={styles['details-loading']}>
                    <LoadingOutlined spin />
                </div>
            )}

            {/* Missing Details Section */}
            {missingRecipients.length > 0 && (
                <MissingDetailsSection
                    missingRecipients={missingRecipients}
                    showPhones={showPhones}
                    showPasswords={showPasswords}
                />
            )}

            <UsersWithDetailsSection
                filledRecipients={filledRecipients}
                showPhones={showPhones}
                showPasswords={showPasswords}
                titleNameSpace={nameSpace}
                headerClassName={styles['uwd-header-override']}
                listClassName={styles['uwd-list-override']}
            />

            {hasBlockedUsers && (
                <BlockedRecipientsSection
                    blockedRecipients={blockedRecipients}
                    getFileName={getFileName}
                    getFileIsFolder={getFileIsFolder}
                    className={styles['blocked-recipients-override']}
                />
            )}
        </div>
    );
});

export default PolicyStep;
